import SEO from '../components/SEO';
import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { FiClock, FiUser, FiArrowRight, FiPlay } from 'react-icons/fi';
import { motion } from 'framer-motion';
import { fetchInfomatics, getImageSrc, formatMarathiDate } from '../apiService';
import { useLanguage } from '../utils/LanguageContext';

const Infomatics = () => {
  const { language } = useLanguage();
  const [infomatics, setInfomatics] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    loadInfomatics();
  }, []);

  const loadInfomatics = async () => {
    try {
      setLoading(true);
      const infomaticsData = await fetchInfomatics();
      setInfomatics(infomaticsData);
    } catch (err) {
      console.error('Error loading infomatics:', err);
      setError('Failed to load infomatics');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return language === 'MR'
      ? date.toLocaleDateString('mr-IN', { year: 'numeric', month: 'long', day: 'numeric' })
      : date.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
  };

  const truncateText = (text, maxLength = 120) => {
    if (!text) return '';
    if (Array.isArray(text)) {
      const fullText = text.join(' ');
      return fullText.length > maxLength ? fullText.substring(0, maxLength) + '...' : fullText;
    }
    return text.length > maxLength ? text.substring(0, maxLength) + '...' : text;
  };

  const isNewInfomatic = (dateString) => {
    const infomaticDate = new Date(dateString);
    const now = new Date();
    const diffTime = Math.abs(now - infomaticDate);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays <= 7;
  };

  const handleCardClick = (infomatic) => {
    if (infomatic.videoUrl) {
      window.open(infomatic.videoUrl, '_blank');
    } else {
      navigate(`/infomatic/${infomatic.slug || infomatic._id}`);
    }
  };

  const InfomaticCard = ({ infomatic, index }) => (
    <motion.div
      className="bg-white border-2 border-red-100 rounded-xl shadow-md hover:shadow-xl hover:border-red-300 transition-all duration-300 overflow-hidden group relative cursor-pointer"
      initial={{ opacity: 0, y: 30 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6, delay: index * 0.1 }}
      whileHover={{ y: -6, scale: 1.02 }}
      whileTap={{ scale: 0.98 }}
      onClick={() => handleCardClick(infomatic)}
    >
      {isNewInfomatic(infomatic.date) && (
        <div className="absolute top-3 right-3 z-10">
          <span className="bg-red-800 text-white px-2 py-1 rounded-full text-xs font-semibold shadow-md">
            {language === 'MR' ? 'नवीन' : 'New'}
          </span>
        </div>
      )}

      {infomatic.videoUrl && (
        <div className="absolute top-3 left-3 z-10">
          <div className="bg-red-800 text-white p-2 rounded-full shadow-md">
            <FiPlay className="w-4 h-4" />
          </div>
        </div>
      )}

      <div className="relative overflow-hidden h-48">
        <img loading="lazy" 
          src={getImageSrc(infomatic.thumbnail)} 
          alt={infomatic.title}
          className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
        
        <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
          <div className="bg-white text-red-800 px-4 py-2 rounded-full font-semibold flex items-center shadow-md">
            {infomatic.videoUrl ? (
              <>
                <FiPlay className="mr-2 w-4 h-4" />
                {language === 'MR' ? 'पाहा' : 'Watch'}
              </>
            ) : (
              <>
                <FiArrowRight className="mr-2 w-4 h-4" />
                {language === 'MR' ? 'वाचा' : 'Read'}
              </>
            )}
          </div>
        </div>
      </div>

      <div className="p-5">
        <h3 className="text-lg font-bold text-red-800 mb-3 line-clamp-2 group-hover:text-red-600 transition-colors leading-tight">
          {infomatic.title}
        </h3>

        {infomatic.description && infomatic.description[0] && (
          <p className="text-red-600 text-sm line-clamp-3 leading-relaxed mb-4">
            {truncateText(infomatic.description, 120)}
          </p>
        )}

        <div className="flex items-center justify-between text-xs text-red-700 mb-4">
          <div className="flex items-center">
            <FiUser className="mr-1 text-red-600 w-3 h-3" />
            <span className="font-medium truncate">{infomatic.author || 'Admin'}</span>
          </div>
          <div className="flex items-center">
            <FiClock className="mr-1 text-red-600 w-3 h-3" />
            <span>{formatDate(infomatic.date)}</span>
          </div>
        </div>

        <div className="text-center">
          <button className="inline-flex items-center gap-2 bg-red-800 hover:bg-red-900 text-white px-4 py-2 rounded-lg font-medium transition-all duration-300">
            {infomatic.videoUrl ? (
              <>
                <FiPlay className="w-4 h-4" />
                {language === 'MR' ? 'व्हिडिओ पाहा' : 'Watch Video'}
              </>
            ) : (
              <>
                <FiArrowRight className="w-4 h-4" />
                {language === 'MR' ? 'संपूर्ण ज्ञानराशी वाचा' : 'Read Full Infomatic'}
              </>
            )}
          </button>
        </div>
      </div>
    </motion.div>
  );

  return (
    <div className="min-h-screen bg-white pt-20">
      <SEO title="इन्फोमॅटिक्स" description="डॉ. राजेंद्र शेंडे यांचे माहितीपूर्ण इन्फोग्राफिक्स आणि लेख." canonical="/allinfomatics" />
      <div className="max-w-7xl mx-auto px-4 py-8">
        <motion.div 
          className="text-center mb-12"
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <h1 className="text-4xl md:text-5xl font-medium text-red-800 mb-4">
            {language === 'MR' ? 'सर्व मराठी ज्ञानराशी' : 'All Marathi Infomatics'}
          </h1>
          <div className="w-24 h-1 bg-red-800 mx-auto mb-4" />
          <p className="text-lg text-red-700 max-w-2xl mx-auto">
            {language === 'MR'
              ? 'आमच्या संपूर्ण मराठी ज्ञानराशी संग्रहातून तुमच्या आवडत्या विषयावर जाणून घ्या'
              : 'Learn about your favorite topics from our complete Marathi infomatics collection'}
          </p>
        </motion.div>

        {loading && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {[...Array(12)].map((_, index) => (
              <div key={index} className="bg-white border-2 border-red-100 rounded-xl shadow-md overflow-hidden animate-pulse">
                <div className="h-48 bg-gray-300"></div>
                <div className="p-5">
                  <div className="h-4 bg-gray-300 rounded mb-3"></div>
                  <div className="h-4 bg-gray-300 rounded mb-3 w-3/4"></div>
                  <div className="h-3 bg-gray-300 rounded mb-2"></div>
                  <div className="h-3 bg-gray-300 rounded w-1/2"></div>
                </div>
              </div>
            ))}
          </div>
        )}

        {error && !loading && (
          <div className="text-center py-12">
            <div className="bg-red-50 border-2 border-red-100 rounded-xl p-8 max-w-md mx-auto">
              <div className="text-6xl text-red-800 mb-4">⚠</div>
              <p className="text-red-800 font-semibold mb-4">
                {language === 'MR' ? 'ज्ञानराशी लोड करताना त्रुटी' : 'Error loading infomatics'}
              </p>
              <button 
                onClick={loadInfomatics}
                className="bg-red-800 hover:bg-red-900 text-white px-6 py-2 rounded-lg font-medium transition-colors"
              >
                {language === 'MR' ? 'पुन्हा प्रयत्न करा' : 'Try Again'}
              </button>
            </div>
          </div>
        )}

        {!loading && !error && infomatics.length === 0 && (
          <motion.div 
            className="text-center py-12"
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6 }}
          >
            <div className="text-6xl mb-4">📚</div>
            <h3 className="text-2xl font-bold text-red-800 mb-2">
              {language === 'MR' ? 'कोणतीही ज्ञानराशी सापडली नाही' : 'No Infomatics Found'}
            </h3>
            <p className="text-red-600">
              {language === 'MR' ? 'सध्या कोणतीही ज्ञानराशी उपलब्ध नाही' : 'No infomatics are currently available'}
            </p>
          </motion.div>
        )}

        {!loading && !error && infomatics.length > 0 && (
          <motion.div 
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.4 }}
          >
            {infomatics.map((infomatic, index) => (
              <InfomaticCard key={infomatic._id} infomatic={infomatic} index={index} />
            ))}
          </motion.div>
        )}

        {!loading && !error && infomatics.length > 0 && (
          <motion.div 
            className="text-center mt-12"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
          >
            <p className="text-red-700 font-medium">
              {language === 'MR'
                ? `एकूण ${infomatics.length} ज्ञानराशी दाखवत आहे`
                : `Showing ${infomatics.length} infomatics`}
            </p>
          </motion.div>
        )}
      </div>
    </div>
  );
};

export default Infomatics;